import { useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Alert,
  SimpleGrid,
  Badge,
  Skeleton,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconFileDescription,
} from '@tabler/icons-react';
/* eslint-disable @typescript-eslint/no-explicit-any */
import PageHeader from '@/components/PageHeader';
import { useExpiringW9Vendors, use1099Readiness } from '@/hooks/usePortalApi';

export default function TaxCompliance() {
  const currentYear = new Date().getFullYear();
  const [taxYear] = useState(currentYear - 1);
  const [daysAhead] = useState(90);

  const { data: expiringW9, isLoading: w9Loading, error: w9Error } = useExpiringW9Vendors(daysAhead);
  const { data: readiness, isLoading: readinessLoading, error: readinessError } = use1099Readiness(taxYear);

  return (
    <Stack gap="lg">
      <PageHeader title="Tax Compliance" description="W-9 tracking, 1099 readiness, and nexus monitoring" />

      {/* W-9 Expiration Tracking */}
      <Paper p="md" withBorder>
        <Group mb="md">
          <ThemeIcon size="lg" color="orange" variant="light"><IconAlertTriangle size={20} /></ThemeIcon>
          <div>
            <Text fw={600} size="lg">Expiring W-9 Forms</Text>
            <Text size="sm" c="dimmed">Vendors with W-9 expiring within {daysAhead} days</Text>
          </div>
        </Group>

        {w9Error ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load W-9 data</Alert>
        ) : w9Loading ? (
          <Stack gap="xs">{[...Array(3)].map((_, i) => <Skeleton key={i} height={40} radius="md" />)}</Stack>
        ) : !expiringW9 || expiringW9.length === 0 ? (
          <Alert color="green" icon={<IconCheck size={16} />}>All vendor W-9 forms are current</Alert>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Vendor</Table.Th>
                <Table.Th>Expiration Date</Table.Th>
                <Table.Th>Days Until Expiry</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expiringW9.map((v: any, i: number) => (
                <Table.Tr key={i}>
                  <Table.Td><Text size="sm" fw={500}>{v.vendor_name || v.name}</Text></Table.Td>
                  <Table.Td><Text size="sm">{v.w9_expiration_date || v.expiration_date || '-'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{v.days_until_expiry ?? '-'}</Text></Table.Td>
                  <Table.Td>
                    <Badge
                      color={(v.days_until_expiry ?? 999) <= 30 ? 'red' : (v.days_until_expiry ?? 999) <= 60 ? 'orange' : 'yellow'}
                      variant="light"
                    >
                      {(v.days_until_expiry ?? 999) <= 30 ? 'Urgent' : (v.days_until_expiry ?? 999) <= 60 ? 'Soon' : 'Upcoming'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* 1099 Readiness */}
      <Paper p="md" withBorder>
        <Group mb="md">
          <ThemeIcon size="lg" color="blue" variant="light"><IconFileDescription size={20} /></ThemeIcon>
          <div>
            <Text fw={600} size="lg">1099 Readiness — Tax Year {taxYear}</Text>
            <Text size="sm" c="dimmed">Vendor eligibility and filing preparation status</Text>
          </div>
        </Group>

        {readinessError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load 1099 readiness data</Alert>
        ) : readinessLoading ? (
          <Stack gap="xs">{[...Array(3)].map((_, i) => <Skeleton key={i} height={40} radius="md" />)}</Stack>
        ) : readiness ? (
          <Stack gap="md">
            {readiness.summary && (
              <SimpleGrid cols={3}>
                <Paper p="sm" bg="green.0" ta="center">
                  <Text size="xl" fw={700} c="green">{readiness.summary.ready ?? 0}</Text>
                  <Text size="xs" c="dimmed">Ready</Text>
                </Paper>
                <Paper p="sm" bg="orange.0" ta="center">
                  <Text size="xl" fw={700} c="orange">{readiness.summary.issues ?? 0}</Text>
                  <Text size="xs" c="dimmed">Has Issues</Text>
                </Paper>
                <Paper p="sm" bg="blue.0" ta="center">
                  <Text size="xl" fw={700} c="blue">{readiness.summary.total ?? 0}</Text>
                  <Text size="xs" c="dimmed">Total Eligible</Text>
                </Paper>
              </SimpleGrid>
            )}

            {readiness.ready && Array.isArray(readiness.ready) && readiness.ready.length > 0 && (
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Vendor</Table.Th>
                    <Table.Th>TIN Status</Table.Th>
                    <Table.Th ta="right">YTD Payments</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {readiness.ready.map((v: any, i: number) => (
                    <Table.Tr key={i}>
                      <Table.Td><Text size="sm" fw={500}>{v.vendor_name || v.name}</Text></Table.Td>
                      <Table.Td><Text size="sm">{v.tin_status || (v.tin ? 'On file' : 'Missing')}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm">${(v.ytd_payments || v.total_payments || 0).toLocaleString()}</Text></Table.Td>
                      <Table.Td>
                        <Badge color={(v.issues?.length || 0) > 0 ? 'orange' : 'green'} variant="light">
                          {(v.issues?.length || 0) > 0 ? `${v.issues.length} issue(s)` : 'Ready'}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}

            {(!readiness.ready && !readiness.summary) && (
              <Text c="dimmed" ta="center" py="md">
                {typeof readiness === 'object' ? JSON.stringify(readiness) : 'No 1099 data available'}
              </Text>
            )}
          </Stack>
        ) : (
          <Text c="dimmed" ta="center" py="md">No 1099 data available</Text>
        )}
      </Paper>
    </Stack>
  );
}
